import { z } from "zod";

/* ===========================================================================
 * US-E17.F6-0193 — BIỂU QUYẾT PHI-EXPENSE (governance) · @UC9268 @UC9269
 *
 * 🔴 GOVERNANCE = quyết định KHÔNG-TIỀN (đổi quy chế / bầu chủ nhóm / đổi cấu hình).
 *    ⇒ contract response **KHÔNG CÓ** `ledgerTxId` (đối lập `castVoteResponseSchema` của expense-vote):
 *    không có bút toán nào để trả về, và việc thiếu field này là CHỐT chống lập lờ "governance có
 *    đụng tiền không". Máy vote (weighted/veto/quorum/silent/tie-break) DÙNG LẠI y nguyên của F2/F3.
 *
 * POST /v1/groups/:id/governance/decisions   — mở phiên (owner/admin/treasurer)
 * POST /v1/governance/decisions/:id/votes    — bỏ/đổi phiếu (mọi member ACTIVE)
 * GET  /v1/governance/decisions/:id          — trạng thái phiên + phiếu của tôi
 * ========================================================================= */

/** Loại chủ thể của 1 phiên `consensus` — cột `Consensus.subjectType`. */
export const CONSENSUS_SUBJECT_TYPE = {
  EXPENSE: "expense",
  GOVERNANCE: "governance",
} as const;

export type ConsensusSubjectType =
  (typeof CONSENSUS_SUBJECT_TYPE)[keyof typeof CONSENSUS_SUBJECT_TYPE];

export const consensusSubjectTypeSchema = z.enum([
  CONSENSUS_SUBJECT_TYPE.EXPENSE,
  CONSENSUS_SUBJECT_TYPE.GOVERNANCE,
]);

/** Loại quyết định governance — cột `Consensus.governanceKind` (CHECK DB cùng whitelist). */
export const GOVERNANCE_KIND = {
  /** Đổi quy chế nhóm (ghi vào `group.settings`, KHÔNG chạm engine tiền). */
  RULE_CHANGE: "rule_change",
  /** Bầu / chuyển chủ nhóm (passed ⇒ reuse OwnershipTransferService). */
  OWNER_TRANSFER: "owner_transfer",
  /** Đổi cấu hình nhóm (ghi vào `group.settings` — override system_settings ở tầng group). */
  CONFIG_CHANGE: "config_change",
} as const;

export type GovernanceKind = (typeof GOVERNANCE_KIND)[keyof typeof GOVERNANCE_KIND];

export const governanceKindSchema = z.enum([
  GOVERNANCE_KIND.RULE_CHANGE,
  GOVERNANCE_KIND.OWNER_TRANSFER,
  GOVERNANCE_KIND.CONFIG_CHANGE,
]);

/** Khoá config governance — TÁCH KHỎI `expense_decision_*` (quyết định phi-tiền có luật riêng). */
export const GOVERNANCE_DECISION_MODE_KEY = "governance_decision_mode";
export const GOVERNANCE_DECISION_THRESHOLD_KEY = "governance_decision_threshold";
export const GOVERNANCE_QUORUM_MIN_KEY = "governance_quorum_min";
export const GOVERNANCE_DEADLINE_HOURS_KEY = "governance_deadline_hours";

/** Cơ chế governance: majority (mặc định) | ratio. `silent`/`admin_only` KHÔNG áp cho governance. */
export const GOVERNANCE_MODES = ["majority", "ratio"] as const;
export type GovernanceMode = (typeof GOVERNANCE_MODES)[number];
export const governanceModeSchema = z.enum(GOVERNANCE_MODES);

export const DEFAULT_GOVERNANCE_MODE: GovernanceMode = "majority";
export const DEFAULT_GOVERNANCE_THRESHOLD = 0.6;
export const DEFAULT_GOVERNANCE_QUORUM_MIN = 0;
export const DEFAULT_GOVERNANCE_DEADLINE_HOURS = 168;

/** Độ dài tối đa key/value config (chống payload phình + injection nhãn). */
export const GOVERNANCE_KEY_MAX = 64;
export const GOVERNANCE_VALUE_MAX = 256;
export const GOVERNANCE_TITLE_MAX = 200;

/**
 * Payload theo loại (discriminated union — validate tại BIÊN, KHÔNG nhận field lạ):
 *  - rule_change / config_change ⇒ `{ key, value }` (value scalar: string | number | boolean).
 *  - owner_transfer              ⇒ `{ newOwnerMemberId }` (GroupMember.id).
 * KHÔNG chứa tiền (B1 không áp — governance phi-tiền) và KHÔNG PII.
 */
const governanceConfigValueSchema = z.union([
  z.string().max(GOVERNANCE_VALUE_MAX),
  z.number(),
  z.boolean(),
]);

export const governanceConfigPayloadSchema = z
  .object({
    key: z.string().min(1).max(GOVERNANCE_KEY_MAX),
    value: governanceConfigValueSchema,
  })
  .strict();

export const governanceOwnerTransferPayloadSchema = z
  .object({
    newOwnerMemberId: z.string().uuid(),
  })
  .strict();

export type GovernanceConfigPayload = z.infer<typeof governanceConfigPayloadSchema>;
export type GovernanceOwnerTransferPayload = z.infer<
  typeof governanceOwnerTransferPayloadSchema
>;

/** Body tạo phiên. Union theo `governanceKind` (superRefine — payload PHẢI khớp loại). */
export const createGovernanceDecisionRequestSchema = z
  .object({
    governanceKind: governanceKindSchema,
    title: z.string().min(1).max(GOVERNANCE_TITLE_MAX),
    payload: z.unknown(),
  })
  .strict()
  .superRefine((data, ctx) => {
    const schema =
      data.governanceKind === GOVERNANCE_KIND.OWNER_TRANSFER
        ? governanceOwnerTransferPayloadSchema
        : governanceConfigPayloadSchema;
    const parsed = schema.safeParse(data.payload);
    if (!parsed.success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["payload"],
        message: "payload không hợp lệ cho loại quyết định này.",
      });
    }
  });

export type CreateGovernanceDecisionRequest = z.infer<
  typeof createGovernanceDecisionRequestSchema
>;

/** Trạng thái phiên governance — CÙNG bộ với consensus (open|passed|failed|expired). */
export const governanceStatusSchema = z.enum(["open", "passed", "failed", "expired"]);
export type GovernanceStatus = z.infer<typeof governanceStatusSchema>;

/** Bộ đếm phiếu (mirror `voteTallySchema` — đếm đầu người + mẫu số SNAPSHOT). */
export const governanceTallySchema = z
  .object({
    yes: z.number().int().nonnegative(),
    no: z.number().int().nonnegative(),
    abstain: z.number().int().nonnegative(),
    /** Mẫu số = eligibleCount SNAPSHOT lúc mở phiên (chống lách người — P3.4). */
    totalEligible: z.number().int().nonnegative(),
  })
  .strict();

export type GovernanceTally = z.infer<typeof governanceTallySchema>;

/**
 * Response tạo phiên / đọc phiên. 🔴 KHÔNG có `ledgerTxId` — governance KHÔNG post ledger.
 * `appliedAt` = mốc ÁP hành động (config ghi / owner đổi); null khi chưa passed hoặc chưa áp.
 */
export const governanceDecisionResponseSchema = z
  .object({
    decisionId: z.string().uuid(),
    groupId: z.string().uuid(),
    subjectType: z.literal(CONSENSUS_SUBJECT_TYPE.GOVERNANCE),
    governanceKind: governanceKindSchema,
    title: z.string(),
    mechanism: governanceModeSchema,
    thresholdRatio: z.number().nullable(),
    status: governanceStatusSchema,
    deadline: z.string().datetime().nullable(),
    tally: governanceTallySchema,
    myVote: z.enum(["yes", "no", "abstain"]).nullable(),
    appliedAt: z.string().datetime().nullable(),
    openedAt: z.string().datetime(),
  })
  .strict();

export type GovernanceDecisionResponse = z.infer<typeof governanceDecisionResponseSchema>;

/** Body bỏ phiếu governance (mirror expense `castVoteRequestSchema`). */
export const castGovernanceVoteRequestSchema = z
  .object({
    choice: z.enum(["yes", "no", "abstain"]),
  })
  .strict();

export type CastGovernanceVoteRequest = z.infer<typeof castGovernanceVoteRequestSchema>;

/**
 * Response bỏ phiếu. 🔴 KHÔNG có `ledgerTxId` (đối lập expense-vote) — governance KHÔNG BAO GIỜ
 * post ledger. `.strict()` ⇒ nếu ai đó "vô tình" nhét ledgerTxId vào, parse FAIL ngay tại biên.
 */
export const castGovernanceVoteResponseSchema = z
  .object({
    decisionId: z.string().uuid(),
    governanceKind: governanceKindSchema,
    myVote: z.enum(["yes", "no", "abstain"]),
    tally: governanceTallySchema,
    decisionStatus: governanceStatusSchema,
    /** Hành động đã ÁP sau passed (true) / chưa (false). failed ⇒ luôn false. */
    applied: z.boolean(),
  })
  .strict();

export type CastGovernanceVoteResponse = z.infer<typeof castGovernanceVoteResponseSchema>;

/** Thông điệp lỗi (VN, không lộ nội bộ). */
export const GOVERNANCE_CLOSED_MESSAGE = "Phiên biểu quyết đã đóng — không thể bỏ phiếu.";
export const GOVERNANCE_NOT_FOUND_MESSAGE = "Phiên biểu quyết không tồn tại.";
